import React, { useState, useEffect } from 'react';
import type { RefObject } from 'react';

import { useFeatureFlag } from 'wanderu-analytics';
import {
    FadeInOut,
    FadeInOutContainer,
} from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    Control,
    SplitTest,
    Variant,
} from 'wanderu-component-split-test';
import { Layout, WDSButton } from 'wanderu-components';
import { MultiDay } from 'wanderu-components-search';
import type { SearchParams } from 'wanderu-components-search';
import {
    CHEAPEST,
    EARLIEST,
    FASTEST,
    LATEST,
    RECOMMENDED,
} from 'wanderu-ducks-search';
import { localize, i18nMoney as ourMoney, i18nMoment as ourMoment } from 'wanderu-i18n';
import { durationToHoursAndMinutes } from 'wanderu-utils';

import arrow from 'wanderu-assets/images/left-black-arrow.svg';
import sliders from 'wanderu-assets/images/sliders.svg';

import { DEPART_DIRECTION } from '../../../constants';
import FilterButton from '../FilterButton/FilterButton';

import styles from './MobileFilters.scss';

import type { TFunction } from 'i18next';

const handleHeaderWidth = ({
    headerRef,
    setHeaderWidth,
}: {
    headerRef: RefObject<HTMLDivElement | null>;
    setHeaderWidth: (width: number) => void
}) => {
    if (headerRef.current) {
        const headerRect = headerRef?.current?.getBoundingClientRect();

        const headerLeftMargin = 19 + 38;

        setHeaderWidth(headerRect.width + headerLeftMargin);
    }
};

const setScrollLeft = ({
    activeSortType,
    searchFiltersRef,
}: {
    activeSortType: string;
    searchFiltersRef: RefObject<HTMLDivElement | null>
}) => {
    let scrollLeft = 0;

    if (
        activeSortType !== RECOMMENDED
        && activeSortType !== CHEAPEST
    ) {
        scrollLeft = searchFiltersRef?.current?.scrollWidth ?? 0;
    }

    if (searchFiltersRef?.current) {
        // eslint-disable-next-line no-param-reassign
        searchFiltersRef.current.scrollLeft = scrollLeft;
    }
};

const renderSortOptionData = ({
    data,
    i18nMoment,
    i18nMoney,
    t,
    type,
}: {
    data: number;
    i18nMoment: typeof ourMoment;
    i18nMoney: typeof ourMoney;
    t: TFunction;
    type: string;
}) => {
    if (
        data === undefined
        || data === 0
    ) {
        return undefined;
    }

    if (type === RECOMMENDED) {
        return t('total-trips', {
            totalTrips: data,
        });
    }

    if (type === FASTEST) {
        const duration = durationToHoursAndMinutes(
            i18nMoment.duration(data),
        );

        if (duration.days > 0) {
            return t('baseApp:trip-duration_days', duration);
        }

        return t('baseApp:trip-duration_hours', duration);
    }

    if (type === CHEAPEST) {
        const price = i18nMoney(data).getTotalPrice();

        return i18nMoney.format(price);
    }

    if (type === EARLIEST || type === LATEST) {
        return i18nMoment.parseZone(data).format('LT');
    }

    return undefined;
};

interface MobileFiltersProps {
    activeSortType: string;
    containerWidth: number;
    departDate: string;
    destinationCityName: string;
    direction: string;
    i18nMoment: typeof ourMoment;
    i18nMoney: typeof ourMoney;
    numberOfActiveFilters: number;
    onMapClick: () => void;
    onSelectSortType: (type: string) => void;
    onToggleFilterMenu: () => void;
    originCityName: string;
    returnDate: string;
    searchParams: SearchParams;
    showMap: boolean;
    sortOptions: {
        [RECOMMENDED]: number;
        [CHEAPEST]: number;
        [FASTEST]: number;
        [EARLIEST]: number;
        [LATEST]: number;
    };
    t: TFunction;
    url: string;
}

const MobileFilters: React.FC<MobileFiltersProps> = ({
    activeSortType,
    containerWidth,
    departDate,
    destinationCityName,
    direction,
    i18nMoment,
    i18nMoney,
    numberOfActiveFilters,
    onMapClick,
    onSelectSortType,
    onToggleFilterMenu,
    originCityName,
    returnDate = '',
    searchParams,
    showMap = false,
    sortOptions,
    t,
    url,
}) => {
    const [headerWidth, setHeaderWidth] = useState<number | undefined>(undefined);
    const [textOverflow, setTextOverflow] = useState(false);

    const { isEnabled: isMultiDayVariant } = useFeatureFlag('multi-day-search');

    const searchFiltersRef = React.createRef<HTMLDivElement>();
    const headerRef = React.createRef<HTMLDivElement>();

    useEffect(() => {
        handleHeaderWidth({ headerRef, setHeaderWidth });
    }, [headerRef, originCityName, destinationCityName]);

    useEffect(() => {
        setScrollLeft({ activeSortType, searchFiltersRef });
    }, [activeSortType, searchFiltersRef]);

    useEffect(() => {
        if (headerWidth && containerWidth) {
            const newTextOverflow = headerWidth >= containerWidth;

            if (newTextOverflow) {
                setTextOverflow(newTextOverflow);
            }
        }
    }, [headerWidth, containerWidth]);

    const returnDateTimestamp = i18nMoment(returnDate).shortDateTimestamp();
    const departDateTimestamp = i18nMoment(departDate).shortDateTimestamp();
    const isDepartDirection = direction === DEPART_DIRECTION;

    const departText = textOverflow
        ? t('to-destination', {
            destinationName: destinationCityName,
        })
        : t('origin-to-destination', {
            destinationName: destinationCityName,
            originName: originCityName,
        });

    const returnText = textOverflow
        ? t('to-destination', {
            destinationName: originCityName,
        })
        : t('origin-to-destination', {
            destinationName: originCityName,
            originName: destinationCityName,
        });

    return (
        <>
            <Layout className={styles['mobile-container']}>
                <A
                    className={styles.arrow}
                    href={url}
                >
                    <img alt="back" src={arrow} />
                </A>

                <FadeInOutContainer
                    animateHeight={false}
                    className={styles['middle-column']}
                    duration={500}
                >
                    <FadeInOut
                        className={styles['middle-column']}
                        show={isDepartDirection}
                    >
                        <h1
                            className={styles['city-to-city']}
                            ref={isDepartDirection
                                ? headerRef
                                : undefined}
                        >
                            {departText}
                        </h1>

                        {isMultiDayVariant
                            ? <span className={styles['departs-on']}>
                                {t<string>('departs-on')}
                            </span>
                            : <span className={styles['depart-date']}>
                                {t<string>('departs-on', { departDate: departDateTimestamp })}
                            </span>
                        }
                    </FadeInOut>

                    <FadeInOut
                        className={styles['middle-column']}
                        show={!isDepartDirection}
                    >
                        <h1
                            className={styles['city-to-city']}
                            ref={!isDepartDirection
                                ? headerRef
                                : undefined}
                        >
                            {returnText}
                        </h1>

                        <span className={styles['depart-date']}>
                            {t<string>('returns-on', {
                                returnDate: returnDateTimestamp,
                            })}
                        </span>
                    </FadeInOut>
                </FadeInOutContainer>
                <SplitTest testName="stationMap">
                    <Control/>
                    <Variant name="showMap">
                        <WDSButton className={styles['map-button']} onClick={onMapClick} variant="unstyled">
                            {showMap
                                ? 'Hide Map'
                                : 'Show Map'}
                        </WDSButton>
                    </Variant>
                </SplitTest>
            </Layout>

            {isMultiDayVariant && <Layout>
                <MultiDay searchParams={searchParams} />

                <hr className={styles.hr} />
            </Layout>}

            <Layout>
                <div
                    className={styles['search-filters']}
                    ref={searchFiltersRef}
                >
                    <button
                        className={styles['filter-button']}
                        data-id="filterButton"
                        onClick={onToggleFilterMenu}
                    >
                        <span className={styles['sort-type']}>
                            {t<string>('filters')}
                        </span>

                        <img
                            alt=""
                            className={styles.sliders}
                            src={sliders}
                        />

                        <span
                            aria-label={t('filters')}
                            aria-live="polite"
                            className={styles['filter-number-container']}
                        >
                            {numberOfActiveFilters > 0 && (
                                <span className={styles['filter-number']}>
                                    {numberOfActiveFilters}
                                </span>
                            )}
                        </span>
                    </button>

                    {Object.entries(sortOptions).map(([type, data]) => (
                        <FilterButton
                            activeSortType={activeSortType}
                            key={type}
                            onSelectSortType={onSelectSortType}
                            sortOptionData={renderSortOptionData({
                                data,
                                i18nMoment,
                                i18nMoney,
                                t,
                                type,
                            })}
                            type={type}
                        />
                    ))}
                </div>
            </Layout>
        </>
    );
};

export { MobileFilters };
export default localize(MobileFilters, [], 'baseApp');
